// components/form/FormContext.tsx
/**
 * FormContext - Provides form validation state management
 * Allows child components to register/unregister and update validation state
 */

import { createContext, useContext } from 'react';

export interface FormContextType {
    // Register a field with the form
    registerField: (name: string, initialValue?: any, rules?: any[], label?: string) => void;

    // Unregister a field from the form
    unregisterField: (name: string) => void;

    // Update validation state of a specific field
    updateFieldValidity: (name: string, isValid: boolean, error?: string) => void;

    // Update the current value of a field (used by fields to sync value)
    updateFieldValue: (name: string, value: any) => void;

    // Get current validation state of a field
    getFieldError: (name: string) => string | undefined;

    // Trigger validation for a specific field
    validateField: (name: string) => void;

    // Helpers to trigger change/blur events from fields
    handleChange: (name: string, value: any, rules?: any[]) => void;
    handleBlur: (name: string, value: any, rules?: any[]) => void;

    // Get all field errors
    getFieldErrors?: () => Record<string, string>;

    // Check if field has error (compatible with useFormValidation)
    hasFieldError?: (name: string) => boolean;

    // Validate all fields (compatible with useFormValidation)
    validateAll?: () => boolean;

    // Reset all validation state
    reset?: () => void;

    // Check if form is valid
    isValid?: () => boolean;

    // Track if user attempted to submit
    attemptedSubmit?: boolean;

    // Optional map of touched fields for compatibility
    touched?: Record<string, boolean>;
}

export const FormContext = createContext<FormContextType | null>(null);

export const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('Form field components must be used within a Form component');
    }
    return context;
};

// Optional: Use this hook when context might not be available
export const useOptionalFormContext = () => {
    return useContext(FormContext);
};

/**
 * Hook to get validation methods
 * Works with both FormContext and useFormValidation hook
 */
export const useValidation = () => {
    const context = useOptionalFormContext();

    return {
        // Error methods
        getFieldError: (name: string) => context?.getFieldError(name) || '',
        hasFieldError: (name: string) => {
            if (context?.hasFieldError) {
                return context.hasFieldError(name);
            }
            const error = context?.getFieldError(name);
            return !!error;
        },

        // Value/change helpers
        updateFieldValue: (name: string, value: any) =>
            context?.updateFieldValue && context.updateFieldValue(name, value),
        handleChange: (name: string, value: any, rules?: any[]) =>
            context?.handleChange && context.handleChange(name, value, rules),
        handleBlur: (name: string, value: any, rules?: any[]) =>
            context?.handleBlur && context.handleBlur(name, value, rules),

        // touched map
        touched: context?.touched || {},

        // Validation methods
        validateField: (name: string) => context?.validateField(name),
        validateAll: () => context?.validateAll?.() || true,

        // State methods
        isValid: () => context?.isValid?.() || true,
        reset: () => context?.reset?.(),

        // Attempted submit flag
        attemptedSubmit: context?.attemptedSubmit || false,

        // Context availability
        hasContext: !!context
    };
};
